import { REDUCED_MARKUP_CATEGORIES } from "@loot-os/fulfillment-core/listing/types";
import { Button } from "@loot-os/ui/components/button";
import { Checkbox } from "@loot-os/ui/components/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@loot-os/ui/components/dialog";
import { Label } from "@loot-os/ui/components/label";
import { useEffect, useState } from "react";

import { ListingTagsPicker } from "./ListingTagsPicker";
import { PromotionPreview } from "./PromotionPreview";
import { TenantsPicker } from "./TenantsPicker";

/** What the dialog hands the promote mutation. */
export interface PromoteListingInput {
  tags: string[];
  tenantIds: string[];
  /** One more row per US M size band for a sneaker; see the checkbox's help text. */
  withSizeBands: boolean;
}

/**
 * Collects the tags, tenants and size band choice a product fetch is promoted with and passes them
 * to the caller's mutation. No tenant means one shared row; each tenant picked
 * gets its own row. The caller owns `open` so the dialog can stay open while
 * the promotion runs and close on success. All three clear on every close, including
 * that one: the route component is shared across product ids, so keeping them
 * would price the next product off the last one's tags.
 *
 * Below the pickers, the dialog lists the rows the promotion would write, with the sizes each
 * keeps, read from the server's preview of the same fetch while the dialog is open.
 *
 * While the promotion runs, no control here closes the dialog. Dismissing one
 * reads as cancelling, and nothing cancels a promotion already sent, so the
 * operator would be left believing a listing they did write never happened.
 */
export function PromoteListingDialog({
  vendorProductKey,
  open,
  disabled,
  isPending,
  onOpenChange,
  onPromote,
}: {
  /** The cached fetch the dialog previews and promotes. */
  vendorProductKey: string;
  open: boolean;
  /** Whether the row can be promoted at all; the server decides, the trigger only reflects it. */
  disabled: boolean;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onPromote: (input: PromoteListingInput) => void;
}) {
  const [tags, setTags] = useState<string[]>([]);
  const [tenantIds, setTenantIds] = useState<string[]>([]);
  const [withSizeBands, setWithSizeBands] = useState(false);
  // Keyed on `open` rather than the close handler, which a success-close does not go through.
  useEffect(() => {
    if (!open) {
      setTags([]);
      setTenantIds([]);
      setWithSizeBands(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(next) => !isPending && onOpenChange(next)}>
      <DialogTrigger
        render={<Button type="button" variant="outline" disabled={disabled || isPending} />}
      >
        Promote to listing
      </DialogTrigger>
      <DialogContent showCloseButton={!isPending}>
        <DialogHeader>
          <DialogTitle>Promote to listing</DialogTitle>
          <DialogDescription>
            Tags decide how the listing is browsed and which markup it takes: tagged only{" "}
            {[...REDUCED_MARKUP_CATEGORIES].join(", ")} it is marked up 1.15x, any other tag or mix
            1.25x. Leave tenants empty for one shared row every tenant sees.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            onPromote({ tags, tenantIds, withSizeBands });
          }}
        >
          <div className="grid gap-2">
            <Label htmlFor="listing-tags">Tags</Label>
            <ListingTagsPicker
              id="listing-tags"
              value={tags}
              disabled={isPending}
              onChange={setTags}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="listing-tenants">Tenants</Label>
            <TenantsPicker
              id="listing-tenants"
              value={tenantIds}
              disabled={isPending}
              onChange={setTenantIds}
            />
          </div>
          <div className="grid gap-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="listing-size-bands"
                checked={withSizeBands}
                disabled={isPending}
                onCheckedChange={(checked) => setWithSizeBands(checked === true)}
              />
              <Label htmlFor="listing-size-bands">Also write one row per US M size band</Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Sneakers only. Each band row keeps the sizes in its band and takes the price of the
              dearest one. Browse shows the product once; boxes and pools can name a band. After the
              first band row, the daily refresh keeps the band rows current.
            </p>
          </div>
          <div className="grid gap-2">
            <Label>Rows and sizes</Label>
            <p className="text-xs text-muted-foreground">
              Shared-catalog price with markup, before shipping; each tenant picked gets the same
              row under its own id. Cost is per size, before markup.
            </p>
            {open && (
              <PromotionPreview
                vendorProductKey={vendorProductKey}
                tags={tags}
                withSizeBands={withSizeBands}
              />
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Promoting…" : "Continue to promote listing"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
